import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Users, Download, Search, Calendar, Phone, Mail, Trophy, TrendingUp, Trash2, Loader2, Filter, X, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const COURSE_OPTIONS = [
  { value: 'all', label: 'All Courses' },
  { value: 'btech', label: 'B.Tech Marine Engineering' },
  { value: 'bsc', label: 'BSc Nautical Science' },
  { value: 'dns', label: 'DNS (Diploma in Nautical Science)' }
];

const CHOICE_OPTIONS = [
  { value: 'any', label: 'Any Choice' },
  { value: '1', label: '1st Choice' },
  { value: '2', label: '2nd Choice' },
  { value: '3', label: '3rd Choice' }
];

function PublicRegistrations() {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [exporting, setExporting] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [courseFilter, setCourseFilter] = useState('all');
  const [choiceFilter, setChoiceFilter] = useState('any');
  const [appliedFilter, setAppliedFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchRegistrations();
  }, []);

  const fetchRegistrations = async (start = startDate, end = endDate, course = courseFilter, choice = choiceFilter, applied = appliedFilter) => {
    setLoading(true);
    try {
      let url = '/public/registrations';
      const params = new URLSearchParams();
      if (start) params.append('start_date', start);
      if (end) params.append('end_date', end);
      if (course && course !== 'all') params.append('course', course);
      if (choice && choice !== 'any') params.append('choice', choice);
      if (applied && applied !== 'all') params.append('has_applied', applied);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await api.get(url);
      setRegistrations(response.data);
    } catch (error) {
      console.error('Error fetching registrations:', error);
      toast.error('Failed to load registrations');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilter = () => {
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      toast.error('Start date cannot be after end date');
      return;
    }
    fetchRegistrations(startDate, endDate, courseFilter, choiceFilter, appliedFilter);
  };

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setCourseFilter('all');
    setChoiceFilter('any');
    setAppliedFilter('all');
    fetchRegistrations('', '', 'all', 'any', 'all');
  };

  const downloadFile = (base64Data, filename) => {
    const link = document.createElement('a');
    link.href = `data:application/octet-stream;base64,${base64Data}`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      let url = '/public/registrations/export/excel';
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (courseFilter && courseFilter !== 'all') params.append('course', courseFilter);
      if (choiceFilter && choiceFilter !== 'any') params.append('choice', choiceFilter);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await api.get(url);
      downloadFile(response.data.data, response.data.filename);
      toast.success('Excel file downloaded successfully');
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this registration? This action cannot be undone.')) {
      return;
    }
    
    setDeleting(true);
    try {
      await api.delete(`/public/registrations/${userId}`);
      toast.success('Registration deleted successfully');
      fetchRegistrations();
      setSelectedIds(selectedIds.filter(id => id !== userId));
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Failed to delete registration');
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      toast.error('No registrations selected');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} registration(s)? This action cannot be undone.`)) {
      return;
    }
    
    setDeleting(true);
    try {
      await api.delete('/public/registrations', { data: selectedIds });
      toast.success(`${selectedIds.length} registration(s) deleted successfully`);
      fetchRegistrations();
      setSelectedIds([]);
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Failed to delete registrations');
    } finally {
      setDeleting(false);
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredRegistrations.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredRegistrations.map(reg => reg.id));
    }
  };

  const toggleSelect = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const filteredRegistrations = registrations.filter(reg => 
    reg.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    reg.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    reg.phone?.includes(searchTerm) ||
    reg.username?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const stats = {
    total: registrations.length,
    attempted: registrations.filter(r => r.total_attempts > 0).length,
    avgScore: registrations.length > 0 
      ? (registrations.reduce((sum, r) => sum + r.average_score, 0) / registrations.length).toFixed(1)
      : 0,
    topScore: Math.max(...registrations.map(r => r.best_score), 0).toFixed(1)
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <div className="spinner"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">MOCK IMU-CET Registrations</h1>
            <p className="text-gray-600">Public registrations and test performance</p>
          </div>
          <div className="flex items-center gap-2">
            {selectedIds.length > 0 && (
              <Button
                onClick={handleBulkDelete}
                disabled={deleting}
                variant="destructive"
                className="flex items-center gap-2"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Delete Selected ({selectedIds.length})
              </Button>
            )}
            <Button
              onClick={handleExportExcel}
              disabled={exporting}
              className="flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              {exporting ? 'Exporting...' : 'Export to Excel'}
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100 mr-4">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Registrations</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100 mr-4">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Attempted Test</p>
                <p className="text-2xl font-bold text-gray-900">{stats.attempted}</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-100 mr-4">
                <Trophy className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Top Score</p>
                <p className="text-2xl font-bold text-gray-900">{stats.topScore}%</p>
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-orange-100 mr-4">
                <TrendingUp className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Average Score</p>
                <p className="text-2xl font-bold text-gray-900">{stats.avgScore}%</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Search and Filters */}
        <Card className="p-4">
          <div className="space-y-4">
            {/* Search Bar */}
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Search by name, email, phone, or username..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 ${showFilters ? 'bg-blue-50 border-blue-300' : ''}`}
              >
                <Filter className="w-4 h-4" />
                Filters
                {(startDate || endDate || courseFilter !== 'all' || choiceFilter !== 'any' || appliedFilter !== 'all') && (
                  <span className="ml-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs">Active</span>
                )}
              </Button>
            </div>

            {/* Filters Panel */}
            {showFilters && (
              <div className="flex flex-wrap items-end gap-4 pt-4 border-t">
                {/* Course Filter */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700 flex items-center gap-1">
                    <GraduationCap className="w-4 h-4" />
                    Course
                  </Label>
                  <Select value={courseFilter} onValueChange={setCourseFilter}>
                    <SelectTrigger className="w-52">
                      <SelectValue placeholder="Select course" />
                    </SelectTrigger>
                    <SelectContent>
                      {COURSE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Choice Filter */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Choice Preference</Label>
                  <Select value={choiceFilter} onValueChange={setChoiceFilter}>
                    <SelectTrigger className="w-36">
                      <SelectValue placeholder="Select choice" />
                    </SelectTrigger>
                    <SelectContent>
                      {CHOICE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Applied to HIMT Filter */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">Applied to HIMT</Label>
                  <Select value={appliedFilter} onValueChange={setAppliedFilter}>
                    <SelectTrigger className="w-32">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Date Filters */}
                <div className="space-y-2">
                  <Label htmlFor="startDate" className="text-sm font-medium text-gray-700">From Date</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-44"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="endDate" className="text-sm font-medium text-gray-700">To Date</Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-44"
                  />
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={handleApplyFilter}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Apply Filters
                  </Button>
                  {(startDate || endDate || courseFilter !== 'all' || appliedFilter !== 'all') && (
                    <Button 
                      variant="outline"
                      onClick={handleClearFilters}
                      className="flex items-center gap-1"
                    >
                      <X className="w-4 h-4" />
                      Clear All
                    </Button>
                  )}
                </div>
                {(startDate || endDate || courseFilter !== 'all' || choiceFilter !== 'any') && (
                  <div className="text-sm text-gray-600 ml-auto flex flex-wrap gap-2">
                    {courseFilter !== 'all' && (
                      <span className="bg-blue-50 px-2 py-0.5 rounded">
                        Course: <span className="font-medium">{COURSE_OPTIONS.find(c => c.value === courseFilter)?.label}</span>
                      </span>
                    )}
                    {choiceFilter !== 'any' && (
                      <span className="bg-purple-50 px-2 py-0.5 rounded">
                        Choice: <span className="font-medium">{CHOICE_OPTIONS.find(c => c.value === choiceFilter)?.label}</span>
                      </span>
                    )}
                    {(startDate || endDate) && (
                      <span className="bg-green-50 px-2 py-0.5 rounded">
                        Date: {' '}
                        <span className="font-medium">
                          {startDate ? new Date(startDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'beginning'}
                        </span>
                        {' - '}
                        <span className="font-medium">
                          {endDate ? new Date(endDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'now'}
                        </span>
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>

        {/* Registrations Table */}
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="py-4 px-4 text-center">
                    <input
                      type="checkbox"
                      checked={selectedIds.length === filteredRegistrations.length && filteredRegistrations.length > 0}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-gray-300"
                    />
                  </th>
                  <th className="text-left py-4 px-4 text-sm font-semibold text-gray-700">#</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold text-gray-700">Name</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold text-gray-700">Contact</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold text-gray-700">Course</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold text-gray-700">Applied to HIMT</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold text-gray-700">Registered</th>
                  <th className="text-center py-4 px-4 text-sm font-semibold text-gray-700">Verified</th>
                  <th className="text-center py-4 px-4 text-sm font-semibold text-gray-700">Score</th>
                  <th className="text-center py-4 px-4 text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRegistrations.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="text-center py-12 text-gray-500">
                      {searchTerm ? 'No registrations match your search' : 'No registrations yet'}
                    </td>
                  </tr>
                ) : (
                  filteredRegistrations.map((reg, index) => (
                    <tr key={reg.id} className={`border-b hover:bg-gray-50 ${selectedIds.includes(reg.id) ? 'bg-blue-50' : ''}`}>
                      <td className="py-4 px-4 text-center">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(reg.id)}
                          onChange={() => toggleSelect(reg.id)}
                          className="w-4 h-4 rounded border-gray-300"
                        />
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-600">{index + 1}</td>
                      <td className="py-4 px-4">
                        <div className="font-medium text-gray-900">{reg.name}</div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Mail className="w-4 h-4" />
                            {reg.email}
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Phone className="w-4 h-4" />
                            {reg.phone}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex flex-col gap-1">
                          {reg.course_choice_1 && (
                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                              reg.course_choice_1 === 'btech' ? 'bg-blue-100 text-blue-800' : 
                              reg.course_choice_1 === 'bsc' ? 'bg-purple-100 text-purple-800' : 
                              reg.course_choice_1 === 'dns' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                            }`}>
                              1st: {reg.course_choice_1 === 'btech' ? 'B.Tech' : 
                                   reg.course_choice_1 === 'bsc' ? 'BSc' : 
                                   reg.course_choice_1 === 'dns' ? 'DNS' : 'N/A'}
                            </span>
                          )}
                          {reg.course_choice_2 && (
                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                              reg.course_choice_2 === 'btech' ? 'bg-blue-50 text-blue-700' : 
                              reg.course_choice_2 === 'bsc' ? 'bg-purple-50 text-purple-700' : 
                              reg.course_choice_2 === 'dns' ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-500'
                            }`}>
                              2nd: {reg.course_choice_2 === 'btech' ? 'B.Tech' : 
                                   reg.course_choice_2 === 'bsc' ? 'BSc' : 
                                   reg.course_choice_2 === 'dns' ? 'DNS' : ''}
                            </span>
                          )}
                          {reg.course_choice_3 && (
                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                              reg.course_choice_3 === 'btech' ? 'bg-blue-50 text-blue-600' : 
                              reg.course_choice_3 === 'bsc' ? 'bg-purple-50 text-purple-600' : 
                              reg.course_choice_3 === 'dns' ? 'bg-green-50 text-green-600' : 'bg-gray-50 text-gray-500'
                            }`}>
                              3rd: {reg.course_choice_3 === 'btech' ? 'B.Tech' : 
                                   reg.course_choice_3 === 'bsc' ? 'BSc' : 
                                   reg.course_choice_3 === 'dns' ? 'DNS' : ''}
                            </span>
                          )}
                          {!reg.course_choice_1 && reg.course_interest && (
                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                              reg.course_interest === 'btech' ? 'bg-blue-100 text-blue-800' : 
                              reg.course_interest === 'bsc' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {reg.course_interest === 'btech' ? 'B.Tech' : 
                               reg.course_interest === 'bsc' ? 'BSc' : 'N/A'}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex flex-col gap-1">
                          {reg.has_applied === true ? (
                            <>
                              <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Yes
                              </span>
                              {reg.application_number && (
                                <span className="text-xs text-gray-600 font-mono">
                                  #{reg.application_number}
                                </span>
                              )}
                            </>
                          ) : reg.has_applied === false ? (
                            <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                              No
                            </span>
                          ) : (
                            <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                              N/A
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Calendar className="w-4 h-4" />
                          {new Date(reg.created_at).toLocaleDateString('en-IN', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <div className="flex flex-col gap-1">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                            reg.email_verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            Email: {reg.email_verified ? '✓' : '✗'}
                          </span>
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                            reg.phone_verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            Phone: {reg.phone_verified ? '✓' : '✗'}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className="font-semibold text-gray-900">{reg.best_score?.toFixed(1) || 0}%</span>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(reg.id)}
                          disabled={deleting}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {filteredRegistrations.length > 0 && (
          <div className="text-sm text-gray-600 text-center">
            Showing {filteredRegistrations.length} of {registrations.length} registrations
          </div>
        )}
      </div>
    </Layout>
  );
}

export default PublicRegistrations;
